import api from './api'

/**
 * Serviço de Dashboard da Plataforma (PLATFORM_OWNER)
 */
export default {
  /**
   * Estatísticas gerais da plataforma
   * Retorna: { totalTenants, totalUsers, activeUsers, activeUsersPercentage }
   */
  getStats() {
    return api.get('/api/platform/dashboard/stats')
  },

  /**
   * Dashboard básico
   * Retorna: { totalTenants, totalPartners, totalUsers }
   */
  getDashboard() {
    return api.get('/api/platform/dashboard')
  },

  /**
   * Tenants criados por mês (últimos 6 meses)
   */
  getTenantsMonthly() {
    return api.get('/api/platform/dashboard/tenants/monthly')
  },

  /**
   * Usuários agrupados por tenant
   * Retorna: [{ tenantName, userCount, tenantId }]
   */
  getUsersByTenant() {
    return api.get('/api/platform/dashboard/users/by-tenant')
  },

  /**
   * Estatísticas de um tenant específico
   * Retorna: { managers, admins, totalUsers }
   */
  getTenantStats(tenantId) {
    return api.get(`/api/platform/dashboard/tenants/${tenantId}/stats`)
  },

  /**
   * Alertas da plataforma
   * Retorna: [{ id, createdAt, icon, severity, description, title }]
   */
  getAlerts() {
    return api.get('/api/platform/dashboard/alerts')
  },

  /**
   * MRR por tenant (faturamento)
   * Retorna: [{ tenant, value }]
   */
  getFinanceByTenant() {
    return api.get('/api/platform/dashboard/finance/by-tenant')
  },

  /**
   * MRR mensal (evolução)
   * Retorna: [{ month, value }]
   */
  getMrrMonthly() {
    return api.get('/api/platform/dashboard/finance/mrr-monthly')
  },

  /**
   * Metadados de todos os tenants (banco control)
   * Retorna: [{ tenantId, companyName, isolationType, databaseName, userCount, status, plan, migrationStatus, provisionedAt, lastSyncAt }]
   */
  getTenantMetadata() {
    return api.get('/api/admin/tenant-metadata')
  },

  /**
   * Detalhes de um tenant
   * Retorna: { id, name, status, plan, maxUsers, companyData, contractInfo, adminEmail, ... }
   */
  getTenantDetail(tenantId) {
    return api.get(`/api/platform/dashboard/${tenantId}`)
  },

  /**
   * Usuários criados por mês (agregado cross-database)
   * Retorna: [{ month, count }]
   */
  getUsersMonthly() {
    return api.get('/api/platform/dashboard/users/monthly')
  },

  /**
   * Funil de conversão
   * Retorna: { trialStarted, trialExpired, converted, conversionRate, avgTrialDays, expiringIn7d, churned, demosRealized, demosScheduled }
   */
  getConversion() {
    return api.get('/api/platform/dashboard/conversion')
  },

  /**
   * Feed de atividade recente consolidado
   * Retorna: [{ type, tenant, description, createdAt, severity }]
   */
  getRecentActivity() {
    return api.get('/api/platform/dashboard/activity/recent')
  },

  /**
   * Score de saúde por tenant
   * Retorna: [{ tenantId, tenantName, score, activityScore, adoptionScore, mrrScore }]
   */
  getHealthScores() {
    return api.get('/api/platform/dashboard/health-scores')
  },

  /**
   * Atividade por tenant (sessões + último login)
   * Retorna: [{ tenantId, tenantName, sessions30d, sessions7d, lastLoginAt, lastLoginUser }]
   */
  getTenantActivity() {
    return api.get('/api/platform/dashboard/tenant-activity')
  },

  /**
   * Força sincronização dos metadados de todos os tenants
   * Retorna: { message: "Sync triggered successfully" }
   */
  syncTenantMetadata() {
    return api.post('/api/admin/tenant-metadata/sync')
  },

  /**
   * KPIs de Engajamento
   * Retorna: { sessionsPerDay, sessionsPerDayAvg7d, avgSessionMinutes, avgSessionMinutesPrev, retentionD7, retentionD30, stickiness, dau, mau }
   */
  getEngagementSummary() {
    return api.get('/api/platform/dashboard/engagement/summary')
  },

  /**
   * Funcionalidades mais usadas por tenants ativos
   * Retorna: [{ feature, percentage, tenantCount, totalTenants }]
   */
  getEngagementFeatures() {
    return api.get('/api/platform/dashboard/engagement/features')
  },

  /**
   * NPS Score, distribuição e evolução mensal
   * Retorna: { score, totalResponses, totalUsers, responseRate, label, promoters, neutrals, detractors, trend, evolution: [{ month, value }] }
   */
  getEngagementNps() {
    return api.get('/api/platform/dashboard/engagement/nps')
  },

  /**
   * Distribuição de sessões por hora do dia (últimos 30 dias)
   * Retorna: [{ hour, sessions }]
   */
  getEngagementPeakHours() {
    return api.get('/api/platform/dashboard/engagement/peak-hours')
  },

  /**
   * Usuários mais ativos cross-tenant (últimos 30 dias)
   * Retorna: [{ name, email, tenant, sessions, avatarUrl }]
   */
  getEngagementTopUsers(limit = 10) {
    return api.get(`/api/platform/dashboard/engagement/top-users?limit=${limit}`)
  },

  /**
   * Saúde financeira (churn rate, inadimplência, NRR, LTV)
   * Retorna: { churnRate, overdueCount, overdueAmount, netRevenueChurn, ltvEstimado }
   */
  getFinanceHealth() {
    return api.get('/api/platform/dashboard/finance/health')
  },

  /**
   * Churn rate mensal (últimos 6 meses)
   * Retorna: [{ month, churnRate }]
   */
  getFinanceChurnMonthly() {
    return api.get('/api/platform/dashboard/finance/churn-monthly')
  },

  /**
   * Faturas recentes
   * Retorna: [{ invoiceId, tenantName, amount, status, date }]
   */
  getFinanceInvoices() {
    return api.get('/api/platform/dashboard/finance/invoices')
  },

  // === ALERTS (real endpoints) ===

  /**
   * Resumo KPI dos alertas
   * Retorna: { openCount, criticalCount, moderateCount, resolvedTodayCount, resolvedTodayTitle, avgResolutionHours, activeRules, totalRules }
   */
  getAlertsSummary() {
    return api.get('/api/platform/dashboard/alerts/summary')
  },

  /**
   * Lista completa de alertas com actions
   * Retorna: [{ id, tenantName, title, description, suggestedAction, severity, status, createdAt, resolvedAt, actions }]
   */
  getAlertsList() {
    return api.get('/api/platform/dashboard/alerts/list')
  },

  /**
   * Marca alerta como resolvido
   */
  resolveAlert(alertId) {
    return api.put(`/api/platform/dashboard/alerts/${alertId}/resolve`)
  },

  /**
   * Ignora/descarta alerta
   */
  dismissAlert(alertId) {
    return api.put(`/api/platform/dashboard/alerts/${alertId}/dismiss`)
  },

  /**
   * Força geração manual de alertas
   */
  generateAlerts() {
    return api.post('/api/platform/dashboard/alerts/generate')
  },

  /**
   * Exclui um alerta
   */
  deleteAlert(alertId) {
    return api.delete(`/api/platform/dashboard/alerts/${alertId}`)
  },

  /**
   * Exclui alertas em lote pelo array de IDs
   */
  deleteAllAlerts(ids = []) {
    return api.delete('/api/platform/dashboard/alerts', { data: ids })
  },
}
